import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { skipToken } from "@reduxjs/toolkit/query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import { styled, SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { debounce } from "lodash";
import { useGetPermissionTemplatesQuery } from "permissions/_api/permission-templates-api-service";
import PermissionTemplateFilterParameters from "permissions/_api/queries/filtering/permission-template-filter-parameters";
import PermissionTemplateOrderParameters from "permissions/_api/queries/ordering/permission-template-order-parameters";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import {
  describeAccess,
  describeResource,
  sortResourcesForDisplay,
} from "permissions/_views/_utilities/resource-descriptions";
import { toPermissionTemplatesResult } from "permissions/_views/_utilities/permission-query-results";
import PermissionTemplate from "permissions/templates/permission-template";
import React, { ReactNode, useEffect, useMemo, useState } from "react";

const TemplatesMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(0.5),
  },
}));
const MenuContainer = styled("div")(({ theme }) => ({
  minWidth: "300px",
  padding: theme.spacing(0.75, 1.5),
}));
const Header = styled("div")(() => ({
  alignItems: "center",
  display: "grid",
  gap: 1,
  gridTemplateColumns: "1fr auto",
}));
const MenuContentContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  maxHeight: "500px",
  maxWidth: "400px",
  overflowY: "auto",
}));
const TemplateItem = styled(ListItem)(({ theme }) => ({
  alignItems: "stretch",
  borderTop: `1px solid ${theme.palette.divider}`,
  flexDirection: "column",
  padding: theme.spacing(0.5, 0),
  "&:first-of-type": { borderTop: "none" },
}));
const TemplateItemHeader = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  columnGap: theme.spacing(1),
  display: "flex",
  width: "100%",
}));
const PreviewRow = styled("div")(({ theme }) => ({
  columnGap: theme.spacing(2),
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  padding: theme.spacing(0.25, 0, 0.25, 1),
}));

export type ApplyTemplateMenuButtonProps = {
  key?: string;
  className?: string;
  disabled?: boolean;
  /** Stretch the button to fill its container (the container controls the width). */
  fullWidth?: boolean;
  sx?: SxProps;
  /** Lets a page that already has a primary action offer this one as a secondary. */
  variant?: "contained" | "outlined";
  /** The button's words. The person page says "Apply template", in the sentence case its other actions use. */
  label?: string;
  onApply?: (template: PermissionTemplate) => void;
};

type TemplateListItemProps = {
  template: PermissionTemplate;
  onApply: (template: PermissionTemplate) => void;
};

/**
 * Lists one template, and expands to show the permissions it holds. Picking a template by
 * name alone means remembering what every template contains; the apply dialog shows the
 * same detail, but only after a choice has been made.
 */
function TemplateListItem(props: Readonly<TemplateListItemProps>) {
  const { template, onApply } = props;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const resources = useMemo(() => sortResourcesForDisplay(template.resources), [template.resources]);
  const grantedCount = resources.filter((resource) => !resource.isDisabled).length;

  return (
    <TemplateItem disableGutters>
      <TemplateItemHeader>
        <ListItemText primary={template.name} secondary={template.description} sx={{ my: 0 }} />
        <Button size="small" onClick={() => onApply(template)}>
          Apply
        </Button>
      </TemplateItemHeader>
      <Button
        aria-expanded={isPreviewOpen}
        aria-label={`Preview ${template.name}`}
        color="inherit"
        endIcon={
          <ExpandMoreIcon
            fontSize="small"
            sx={{ transform: isPreviewOpen ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
          />
        }
        size="small"
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
        onClick={() => setIsPreviewOpen(!isPreviewOpen)}
      >
        {grantedCount} permission{grantedCount === 1 ? "" : "s"}
      </Button>
      <Collapse unmountOnExit in={isPreviewOpen}>
        {resources.length ? (
          resources.map((resource, index) => (
            // Keyed by position: a template may hold two resources of one type, told apart
            // only by a qualifier the key would have to rebuild.
            <PreviewRow key={`${index}-${resource.resourceType}`}>
              <Typography variant="caption">{describeResource(resource)}</Typography>
              <Typography color={resource.isDisabled ? "error" : "text.secondary"} variant="caption">
                {resource.isDisabled ? "Not granted" : describeAccess(resource)}
              </Typography>
            </PreviewRow>
          ))
        ) : (
          <Typography color="text.secondary" variant="caption">
            This template holds no permissions.
          </Typography>
        )}
      </Collapse>
    </TemplateItem>
  );
}

export default function ApplyTemplateMenuButton(props: Readonly<ApplyTemplateMenuButtonProps>): ReactNode {
  const { className, disabled, fullWidth, sx, onApply } = props;
  const variant = props.variant ?? "contained";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const open = Boolean(anchorEl);
  const debouncedSearch = useMemo(() => debounce((value: string) => setSearch(value), 300), []);
  const permissionTemplatesQuery = useMemo(
    () =>
      new PermissionTemplateQuery({
        filterParameters: new PermissionTemplateFilterParameters({ name: search }),
        orderParameters: new PermissionTemplateOrderParameters({}),
        paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
      }),
    [search],
  );
  const permissionTemplatesQueryState = useGetPermissionTemplatesQuery(
    open ? { query: permissionTemplatesQuery } : skipToken,
  );
  const permissionTemplatesResponse = useMemo(
    () => toPermissionTemplatesResult(permissionTemplatesQueryState.data),
    [permissionTemplatesQueryState.data],
  );
  const isLoadingPermissionTemplates = permissionTemplatesQueryState.isLoading;
  const permissionTemplatesLoadErrors = permissionTemplatesQueryState.isError;

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  function handleMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleApplyTemplate(template: PermissionTemplate) {
    onApply?.(template);
    handleClose();
  }

  return (
    <Box className={className} sx={sx}>
      <Button
        startIcon={<AutoFixHighIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        variant={variant}
        disabled={disabled || open}
        fullWidth={fullWidth}
        color="primary"
        disableElevation
        onClick={handleMenuOpen}
      >
        {props.label ?? "Apply Template"}
      </Button>
      <TemplatesMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={0}
      >
        <MenuContainer>
          <Header>
            <TextField
              placeholder="Search templates"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          debouncedSearch.cancel();
                          setSearchText("");
                          setSearch("");
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              size="small"
              fullWidth
              variant="standard"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
          </Header>
          <MenuContentContainer>
            <List disablePadding dense>
              {isLoadingPermissionTemplates && (
                <ListItem disableGutters>
                  <ListItemText primary="Loading templates..." />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates && permissionTemplatesResponse?.data.length === 0 && (
                <ListItem disableGutters>
                  <ListItemText primary="No templates available" />
                </ListItem>
              )}
              {permissionTemplatesLoadErrors && (
                <ListItem disableGutters>
                  <ListItemText primary="Error loading templates" />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates &&
                (permissionTemplatesResponse?.data ?? []).map((template) => (
                  <TemplateListItem key={template.id.value} template={template} onApply={handleApplyTemplate} />
                ))}
            </List>
          </MenuContentContainer>
        </MenuContainer>
      </TemplatesMenu>
    </Box>
  );
}
